import React from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { FormField, ImageOptions } from '../../types/form';

interface ImageFieldEditorProps {
  field: FormField;
  editedField: FormField;
  onFieldChange: (updates: Partial<FormField>) => void;
}

export const ImageFieldEditor: React.FC<ImageFieldEditorProps> = ({
  field,
  editedField,
  onFieldChange,
}) => {
  const options = (editedField.options || {}) as ImageOptions;
  
  const updateOptions = (updates: any) => {
    onFieldChange({
      options: {
        ...editedField.options,
        ...updates
      }
    });
  };

  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor={`layout-${field.id}`}>Layout</Label>
        <Select
          value={options.layout || 'single'}
          onValueChange={(value) => updateOptions({ layout: value })}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select layout" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="single">Single Image</SelectItem>
            <SelectItem value="side-by-side">Two Images Side by Side</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center space-x-2">
        <Checkbox
          id={`aboveTitle-${field.id}`}
          checked={options.aboveTitle || false}
          onCheckedChange={(checked) => updateOptions({ aboveTitle: checked === true })}
        />
        <Label htmlFor={`aboveTitle-${field.id}`}>Show above form title</Label>
      </div>

      <div className="flex items-center space-x-2">
        <Checkbox
          id={`showBorder-${field.id}`}
          checked={options.showBorder !== false}
          onCheckedChange={(checked) => updateOptions({ showBorder: checked === true })}
        />
        <Label htmlFor={`showBorder-${field.id}`}>Show border</Label>
      </div>

      <div>
        <Label htmlFor={`imageSize-${field.id}`}>Image Size</Label>
        <Select
          value={options.imageSize || 'medium'}
          onValueChange={(value) => updateOptions({ imageSize: value })}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select size" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="small">Small (150px)</SelectItem>
            <SelectItem value="medium">Medium (300px)</SelectItem>
            <SelectItem value="large">Large (500px)</SelectItem>
            <SelectItem value="full">Full Width</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor={`imageSource-${field.id}`}>Image Source {options.layout === 'side-by-side' ? '(Left)' : ''}</Label>
        <Select
          value={options.imageSource || 'url'}
          onValueChange={(value) => updateOptions({ imageSource: value, imageUrl: '', imageFile: null })}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select image source" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="url">Image URL</SelectItem>
            <SelectItem value="file">Upload File</SelectItem>
          </SelectContent>
        </Select>

        {options.imageSource === 'url' ? (
          <div className="mt-2">
            <Input
              id={`imageUrl-${field.id}`}
              type="url"
              value={options.imageUrl || ''}
              onChange={(e) => updateOptions({ imageUrl: e.target.value })}
              placeholder="https://example.com/image.jpg"
            />
          </div>
        ) : (
          <div className="mt-2">
            <Input
              id={`imageFile-${field.id}`}
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  const reader = new FileReader();
                  reader.onload = () => {
                    updateOptions({
                      imageFile: reader.result as string,
                      imageFileName: file.name
                    });
                  };
                  reader.readAsDataURL(file);
                }
              }}
            />
            {options.imageFileName && (
              <div className="mt-1 text-xs text-muted-foreground">
                Selected: {options.imageFileName}
              </div>
            )}
          </div>
        )}

        <div className="mt-2">
          <Label htmlFor={`imageAlt-${field.id}`}>Alt Text {options.layout === 'side-by-side' ? '(Left)' : ''}</Label>
          <Input
            id={`imageAlt-${field.id}`}
            type="text"
            value={options.imageAlt || ''}
            onChange={(e) => updateOptions({ imageAlt: e.target.value })}
            placeholder="Image description for accessibility"
          />
        </div>
      </div>

      {options.layout === 'side-by-side' && (
        <div>
          <Label htmlFor={`imageSource2-${field.id}`}>Right Image Source</Label>
          <Select
            value={options.imageSource2 || 'url'}
            onValueChange={(value) => updateOptions({ imageSource2: value, imageUrl2: '', imageFile2: null })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select right image source" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="url">Image URL</SelectItem>
              <SelectItem value="file">Upload File</SelectItem>
            </SelectContent>
          </Select>

          {options.imageSource2 === 'url' ? (
            <div className="mt-2">
              <Input
                id={`imageUrl2-${field.id}`}
                type="url"
                value={options.imageUrl2 || ''}
                onChange={(e) => updateOptions({ imageUrl2: e.target.value })}
                placeholder="https://example.com/image2.jpg"
              />
            </div>
          ) : (
            <div className="mt-2">
              <Input
                id={`imageFile2-${field.id}`}
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    const reader = new FileReader();
                    reader.onload = () => {
                      updateOptions({
                        imageFile2: reader.result as string,
                        imageFileName2: file.name
                      });
                    };
                    reader.readAsDataURL(file);
                  }
                }}
              />
              {options.imageFileName2 && (
                <div className="mt-1 text-xs text-muted-foreground">
                  Selected: {options.imageFileName2}
                </div>
              )}
            </div>
          )}

          <div className="mt-2">
            <Label htmlFor={`imageAlt2-${field.id}`}>Right Alt Text</Label>
            <Input
              id={`imageAlt2-${field.id}`}
              type="text"
              value={options.imageAlt2 || ''}
              onChange={(e) => updateOptions({ imageAlt2: e.target.value })}
              placeholder="Right image description for accessibility"
            />
          </div>
        </div>
      )}
    </div>
  );
};
